import { firebaseService } from "../lib/firebase";
import { logger } from "../utils/logger";
import { useAuthStore } from "../stores/useAuthStore";
import type { IdxConsentReceipt } from "../config/idxConsentReceipts";

interface RegistrationData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  marketingConsent: boolean;
  consentReceipts?: IdxConsentReceipt[];
  gaClientId?: string;
  agent?: string; // Agent email from URL parameters
  tenantId?: string; // Tenant ID from the config
}

export const authService = {
  /**
   * Register a new user with Firebase Auth and store their profile data
   * for AuthProvider to handle during auth state change.
   */
  register: async (data: RegistrationData) => {
    logger.debug("[Registration Flow]", { code: "REGISTRATION_STARTED" });

    // Hand the registration data to profile resolution via the in-memory store
    // (session-scoped; survives the Firebase sign-in round-trip without touching
    // localStorage, so it can't go stale across visits).
    useAuthStore.getState().setPendingRegistration({
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      marketingConsent: data.marketingConsent,
      consentReceipts: data.consentReceipts,
      gaClientId: data.gaClientId,
      agent: data.agent,
      tenantId: data.tenantId,
    });

    try {
      // Check if user already exists
      const existingUser = await firebaseService.auth.getCurrentUser();
      let userCredential;

      if (existingUser && existingUser.email === data.email) {
        // User exists, just return their credentials
        userCredential = { user: existingUser };
        logger.debug("[Registration Flow]", { code: "EXISTING_FIREBASE_USER" });
      } else {
        // Create new Firebase auth user with display name
        userCredential = await firebaseService.auth.signUp({
          email: data.email,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
        });
      }

      logger.debug("[Registration Flow]", { code: "FIREBASE_USER_READY" });

      // Verify the user exists in Firebase Auth using auth.currentUser directly
      // Import auth from firebase
      const { auth } = await import("../lib/firebase");
      const currentUser = auth.currentUser;

      if (!currentUser || currentUser.uid !== userCredential.user.uid) {
        logger.error("[Registration Flow]", {
          code: "FIREBASE_USER_VERIFICATION_FAILED",
        });
        throw new Error("Failed to verify user creation");
      }

      logger.debug("[Registration Flow]", { code: "FIREBASE_USER_VERIFIED" });

      return userCredential;
    } catch (error) {
      logger.error("[Registration Flow]", { code: "REGISTRATION_FAILED" });
      throw error;
    }
  },

  /**
   * Clear the pending registration data.
   */
  clearRegistrationData: () => {
    useAuthStore.getState().clearPendingRegistration();
  },

  /**
   * Get the pending registration data captured at the register step.
   */
  getRegistrationData: () => {
    return useAuthStore.getState().pendingRegistration;
  },
};
